import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Tag, Empty, Input, Select, Card, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { Environment, EnvironmentWithMetrics, DeploymentRuntimeInfo, Host, GetHostResponse } from '@src/api/deployment/deploymentApi';
import deploymentApi from '@src/api/deployment/deploymentApi';
import useTranslation from '@hooks/useTranslation';
import styles from './ManageEnvironmentsModal.module.css';

const { Search } = Input;

type HostData = {
	host: Host & {
		failedDeployments?: string[];
	};
	metrics?: GetHostResponse['latestHeartbeat'];
	deployments: DeploymentRuntimeInfo[];
};

type EnvironmentData = {
	environment: EnvironmentWithMetrics;
	hosts: Record<string, HostData>;
};

type EnvironmentsTabProps = {
	visible?: boolean;
	refreshTrigger?: number;
	onEditEnvironment: (environment: Environment) => void;
	onDeleteEnvironment: (environmentId: string) => void;
};

/**
 * Computes metrics for an environment
 * @param envData - Environment data to compute metrics for
 * @returns computed metrics
 */
const computeEnvironmentMetrics = (envData: EnvironmentData) => {
	const hosts = Object.values(envData.hosts);
	const totalHosts = hosts.length;
	const env = envData.environment;
	const totalDeployments = env.activeDeployments.length;
	const totalProcesses = env.activeProcesses;
	return { totalHosts, totalDeployments, totalProcesses };
};

/**
 * Renders environment tags
 * @param environment - Environment to render tags for
 */
const renderEnvironmentTags = (environment: Environment) => {
	if (!environment.tags || Object.keys(environment.tags).length === 0) return null;
	return Object.entries(environment.tags).map(([key, value]) => (
		<Tag key={key} className={styles.Tag}>
			{key}: {value}
		</Tag>
	));
};

/**
 * Environments tab component displaying environment cards with filters
 * @param visible - Whether the tab is currently visible
 * @param refreshTrigger - Number that changes to trigger a refresh
 * @param onEditEnvironment - Callback to edit an environment
 * @param onDeleteEnvironment - Callback to delete an environment
 */
const EnvironmentsTab: React.FC<EnvironmentsTabProps> = ({
	visible,
	refreshTrigger,
	onEditEnvironment,
	onDeleteEnvironment,
}) => {
	const t = useTranslation('ManageEnvironmentsModal');
	const tCommon = t.withBaseKey('CommonWords');

	const [environments, setEnvironments] = useState<Record<string, EnvironmentData>>({});
	const [searchText, setSearchText] = useState('');
	const [environmentFilter, setEnvironmentFilter] = useState<string>('all');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [loading, setLoading] = useState(false);

	/**
	 * Loads all environments
	 */
	const loadEnvironments = useCallback(async () => {
		setLoading(true);
		try {
			const envs: EnvironmentWithMetrics[] = await deploymentApi.listEnvironments();
			const envData: Record<string, EnvironmentData> = {};
			
			for (const env of envs) {
				// Use hosts from the response - no need to fetch details since UI only shows aggregate metrics
				const hostData: Record<string, HostData> = {};
				for (const host of env.hosts) {
					hostData[host.id] = {
						host,
						deployments: [],
					};
				}
				
				envData[env.id] = {
					environment: env, // EnvironmentWithMetrics extends Environment
					hosts: hostData,
				};
			}
			
			setEnvironments(envData);
		} catch (err) {
			console.error('Failed to load environments:', err);
		} finally {
			setLoading(false);
		}
	}, []);

	// Load environments when tab becomes visible or refreshTrigger changes
	useEffect(() => {
		if (visible) {
			loadEnvironments();
		}
	}, [visible, refreshTrigger, loadEnvironments]);

	/**
	 * Filters environments based on search text and filters
	 */
	const filteredEnvironments = useMemo(() => {
		return Object.values(environments).filter(envData => {
			// Search filter
			if (searchText) {
				const searchLower = searchText.toLowerCase();
				const matchesSearch = (
					envData.environment.name.toLowerCase().includes(searchLower) ||
					envData.environment.description?.toLowerCase().includes(searchLower) ||
					Object.entries(envData.environment.tags || {}).some(([key, value]) =>
						key.toLowerCase().includes(searchLower) || value.toLowerCase().includes(searchLower)
					)
				);
				if (!matchesSearch) return false;
			}

			// Environment filter
			if (environmentFilter !== 'all') {
				if (environmentFilter !== envData.environment.id) return false;
			}

			// Status filter (based on whether environment has active deployments)
			if (statusFilter !== 'all') {
				const hosts = Object.values(envData.hosts);
				const hasActiveDeployments = hosts.some(hostData => 
					hostData.deployments.some(d => d.status === 'active')
				);
				if (statusFilter === 'active' && !hasActiveDeployments) return false;
				if (statusFilter === 'inactive' && hasActiveDeployments) return false;
			}

			return true;
		});
	}, [environments, searchText, environmentFilter, statusFilter]);

	return (
		<>
			<div className={styles.EnvironmentsFiltersContainer}>
				<Search
					placeholder={t('Environments.SearchPlaceholder')}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					allowClear
					className={styles.EnvironmentsSearch}
				/>
				<Select
					value={environmentFilter}
					onChange={setEnvironmentFilter}
					className={styles.EnvironmentsFilter}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllEnvironments')}</Select.Option>
					{Object.values(environments).map(envData => (
						<Select.Option key={envData.environment.id} value={envData.environment.id}>
							{envData.environment.name}
						</Select.Option>
					))}
				</Select>
				<Select
					value={statusFilter}
					onChange={setStatusFilter}
					className={styles.EnvironmentsFilter}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllStatuses')}</Select.Option>
					<Select.Option value="active">{tCommon('Active')}</Select.Option>
					<Select.Option value="inactive">{tCommon('Inactive')}</Select.Option>
				</Select>
			</div>
			{filteredEnvironments.length === 0 ? (
				<Empty description={t('Environments.NoEnvironments')} />
			) : (
				<div className={styles.EnvironmentsGrid}>
					{filteredEnvironments.map(envData => {
						const env = envData.environment;
						const metrics = computeEnvironmentMetrics(envData);
						return (
							<Card key={env.id} className={styles.EnvironmentCard}>
								<div className={styles.EnvironmentCardHeader}>
									<div className={styles.EnvironmentCardInfo}>
										<div className={styles.EnvironmentCardName}>
											{env.name}
											{env.private && (
												<Tooltip title={t('Environments.Private')}>
													<LockOutlined className={styles.PrivateIcon} />
												</Tooltip>
											)}
										</div>
										<div className={styles.EnvironmentCardId}>
											<span className={styles.EnvironmentCardIdLabel}>ID:</span>
											<span className={styles.EnvironmentCardIdValue}>{env.id}</span>
										</div>
										{env.description && (
											<div className={styles.EnvironmentCardDescription}>{env.description}</div>
										)}
									</div>
									<div className={styles.EnvironmentCardActions}>
										<Button
											type="text"
											icon={<EditOutlined />}
											onClick={() => onEditEnvironment(env)}
											title={t('Environments.Edit')}
										/>
										<Popconfirm
											title={t('Environments.DeleteConfirm.Title')}
											description={t('Environments.DeleteConfirm.Description', { name: env.name })}
											onConfirm={() => onDeleteEnvironment(env.id)}
											okText={t('Environments.DeleteConfirm.Confirm')}
											cancelText={t('Environments.DeleteConfirm.Cancel')}
											okButtonProps={{ danger: true }}
										>
											<Button
												type="text"
												danger
												icon={<DeleteOutlined />}
												title={t('Environments.Delete')}
											/>
										</Popconfirm>
									</div>
								</div>
								{env.tags && Object.keys(env.tags).length > 0 ? (
									<div className={styles.EnvironmentCardTags}>
										{renderEnvironmentTags(env)}
									</div>
								) : (
									<div className={styles.NoTagsSpace} />
								)}
								<div className={styles.EnvironmentCardMetrics}>
									<div className={styles.EnvironmentMetric}>
										<span className={styles.EnvironmentMetricValue}>{metrics.totalHosts}</span>
										<span className={styles.EnvironmentMetricLabel}>{t('Environments.Metrics.Hosts')}</span>
									</div>
									{/* <div className={styles.EnvironmentMetric}>
										<span className={classNames(styles.EnvironmentMetricValue, styles.EnvironmentMetricValueActive)}>
											{metrics.activeHosts}
										</span>
										<span className={styles.EnvironmentMetricLabel}>{t('Environments.Metrics.Active')}</span>
									</div> */}
									<div className={styles.EnvironmentMetric}>
										<span className={styles.EnvironmentMetricValue}>{metrics.totalDeployments}</span>
										<span className={styles.EnvironmentMetricLabel}>{t('Environments.Metrics.Deployments')}</span>
									</div>
									<div className={styles.EnvironmentMetric}>
										<span className={styles.EnvironmentMetricValue}>{metrics.totalProcesses}</span>
										<span className={styles.EnvironmentMetricLabel}>{t('Environments.Metrics.Processes')}</span>
									</div>
								</div>
							</Card>
						);
					})}
				</div>
			)}
		</>
	);
};

export default EnvironmentsTab;

